import { isEmpty } from 'lodash';

import { OwnedObject, invalidateOwner, deg2rad,
         setLineDash, Magnitude } from 'gear';

import { Color } from './color';
import { Xml } from '../util';

export const PenBase = OwnedObject.extend({ typeName: 'PenBase', __debugProps: true });

// dashStyle: Solid, Dash, Dot, DashDot, DashDotDot
// startCap: Flat, Square, Round, Triangle, NoAnchor, SquareAnchor, RoundAnchor, DiamondAnchor, ArrowAnchor, AnchorMask
// endCap: (see above)
// dashCap: Flat, Round, Triangle
// lineJoin: Miter, Bevel, Round, MiterClipped

export const Pen = PenBase.extend({ typeName: 'Pen',

  type: 'Pen',

  Properties: {
    color      : { type: 'Color'      , def: Color.Transparent()                  , onChange:invalidateOwner, owned:true },
    width      : { type: 'magnitude'  , def:new Magnitude(0,'mm') , dropDef:true  , onChange:invalidateOwner },
    dashStyle  : { type: 'string'     , def: 'Solid'              , dropDef:true  , onChange:invalidateOwner },
    startCap   : { type: 'string'     , def: 'Flat'               , dropDef:true  , onChange:invalidateOwner },
    endCap     : { type: 'string'     , def: 'Flat'               , dropDef:true  , onChange:invalidateOwner },
    dashCap    : { type: 'string'     , def: 'Flat'               , dropDef:true  , onChange:invalidateOwner },
    lineJoin   : { type: 'string'     , def: 'Round'                              , onChange:invalidateOwner },
    miterLimit : { type: 'number'     , def: 10.0                 , dropDef:true  , onChange:invalidateOwner },
    behindFill : { type: 'boolean'    , def: true                 , dropDef:true  , onChange:invalidateOwner },
    name       : { type: 'string'     , def: null                 , dropDef: true , onChange:invalidateOwner },
    tag        : { type: 'string'     , def: null                 , dropDef: true , onChange:invalidateOwner },
    label      : { type: 'string'     , def: null                 , dropDef: true , onChange:invalidateOwner }
  },
  empty() { return false; },
  _empty: false,
  _type : 'Pen',

  isEqual ( other) { return this.propertiesAreEqual(other); },
  isNear( other,eps) { // Quite hacky
    if( Math.abs( this._width-other._width ) > eps ) {
      return false;
    }
    const thisWidth = this._width, otherWidth = other._width;
    this._width = new Magnitude(0,'mm'); other._width = new Magnitude(0,'mm');
    const near = this.propertiesAreEqual(other);
    this._width = thisWidth; other._width = otherWidth;
    return near;
  },
  setupContext( context,matrix) {
    context.strokeStyle = this._color.rgba;
    const w = context.lineWidth = this._width * ( matrix ? matrix.scaleFactor() : 1.0 );
    let miterLimit, lineJoin;
    if( this._lineJoin === 'AdaptiveMiter' ) {
      // In Corel, the Miter Limit is an angle limit. If the corner has an angle smaller than the
      // limit it gets belved.
      const miterAngleLimit = this._miterLimit;

      // We are using the GDI definition (same as the SVG definition), see:
      // http://msdn.microsoft.com/en-us/library/system.drawing.pen.miterlimit.aspx

      // We can express:
      // MiterLimit = miterLength/penWidth = sin( miterAngleLimit/2 )
      const directMiterLimit = 1.0 / Math.sin( deg2rad(miterAngleLimit) / 2 ) ;

      // Corel belves the angle heavily if you get past that angle. So the miter limit
      // should be reduced to start producing its smooth effect before we reach that angle.
      const convertionFactor = 1.0; // 0.55 ;

      miterLimit = convertionFactor * directMiterLimit ;
      lineJoin = 'miter';
    }
    else {
      miterLimit = this._miterLimit;
      lineJoin = this._lineJoin.toLowerCase();
    }
    context.lineJoin = lineJoin;
    context.miterLimit = miterLimit;

    const cap = this._dashCap.toLowerCase(); // Flat is the default, Triangle is not supported.

    let capCorrection = 0;
    if( cap === 'round' ) {
      context.lineCap = cap;
      capCorrection = w * 0.9;
    }

    const dashStyle = this._dashStyle;
    const dash  = 3*w - capCorrection,
          space =   w + capCorrection,
          dot   =   w - capCorrection;
    if( dashStyle !== 'Solid' ) {
      setLineDash( context,
        dashStyle === 'Dash'       ? [dash,space]
      : dashStyle === 'Dot'        ? [dot,space]
      : dashStyle === 'DashDot'    ? [dash,space,dot,space]
      : dashStyle === 'DashDotDot' ? [dash,space,dot,space,dot,space]
                                   : [] );
    }
  },
  getType() {
    return this._type;
  },
  getColors( colors = [] ) {
    colors.push(this._color);
    return colors;
  },
  invalidate: invalidateOwner
});


// We use 'Pen' in object types to be able to construct a default brush
// Overwrite the Base construct. We need to review this
Pen.construct = function(v) {
  if( v === null ) {
    return v;
  }
  else if( v instanceof PenBase ) {
    return v.adopted(this);
  }
  else {
    return ( new Pen(v) ).owner(this);
  }
};


Pen.fromWidthRgb = function(width,rgb,alpha) {
  return new Pen({width:new Magnitude(width,'mm'),color:Color.fromRgb(rgb,alpha)});
};

export const CutPen = PenBase.extend({ typeName: 'CutPen',

  type: 'CutPen',

  Properties: {
    color : { type: 'Color', def: Color.CutPen(), onChange:invalidateOwner, owned:true },
    name       : { type: 'string'  , def: null, dropDef: true, onChange: invalidateOwner },
    tag        : { type: 'string'  , def: null, dropDef: true, onChange: invalidateOwner },
    label      : { type: 'string'  , def: null, dropDef: true, onChange: invalidateOwner }
  },

  behindFill() {
    return false;
  },
  empty() {
    return false;
  },
  _empty: false,
  _type : 'CutPen',
  _width : new Magnitude(0,'mm'),

  width( v ) {
    return v===undefined ? new Magnitude(0,'mm') : this;  // [TODO] Review this idiom
  },

  isEqual( other ) {
    return this.propertiesAreEqual(other);
  },
  getType() {
    return this._type;
  },
  getColors( colors = [] ) {
    return colors;
  },
  setupContext( context ) {
    context.strokeStyle = this._color.rgba;
    context.lineWidth = 1;
  },
  invalidate: invalidateOwner
});
Xml.addFromBuild(CutPen,'CutPen');
Xml.addToXml(CutPen);


export const DisplayPen = PenBase.extend({ typeName: 'DisplayPen',

  type: 'DisplayPen',

  // Should we add a color as a hint to the apps?
  Properties: {
  // color : { type: 'Color', def: Color.CutPen(), onChange:invalidateOwner, owned:true }
    xType : { type: 'string', def: null, onChange:invalidateOwner },
    name  : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    tag   : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner },
    label : { type: 'string', def: null, dropDef: true, onChange: invalidateOwner }
  },

  behindFill() { return false; },
  empty() { return false; },
  _empty: false,
  _type : 'DisplayPen',
  _width : new Magnitude(0,'mm'),
  width ( v ) { return v===undefined ? new Magnitude(0,'mm') : this; }, // [TODO] Review this idiom

  isEqual ( other) { return this.propertiesAreEqual(other); },
  getType() { return this._type; },
  getColors( colors = [] ) {
    return colors;
  },
  invalidate: invalidateOwner
});
Xml.addFromBuild(DisplayPen,'DisplayPen');
Xml.addToXml(DisplayPen);

export const MaskPen = PenBase.extend({ typeName: 'MaskPen',

  type: 'MaskPen',

  // Should we add a color as a hint to the apps?
  Properties: {
  // color : { type: 'Color', def: Color.CutPen(), onChange:invalidateOwner, owned:true }
    xType : { type: 'string', def: null, onChange:invalidateOwner },
    name  : { type: 'string'  , def: null, dropDef: true, onChange: invalidateOwner },
    tag   : { type: 'string'  , def: null, dropDef: true, onChange: invalidateOwner },
    label : { type: 'string'  , def: null, dropDef: true, onChange: invalidateOwner }
  },

  behindFill() { return false; },
  empty() { return false; },
  _empty: false,
  _type : 'MaskPen',
  _width : new Magnitude(0,'mm'),
  width ( v ) { return v===undefined ? new Magnitude(0,'mm') : this; }, // [TODO] Review this idiom

  isEqual ( other) { return this.propertiesAreEqual(other); },
  getType() { return this._type; },
  getColors( colors = [] ) {
    return colors;
  },
  invalidate: invalidateOwner
});
Xml.addFromBuild(MaskPen,'MaskPen');
Xml.addToXml(MaskPen);

export const EmptyPen = PenBase.extend({ typeName: 'EmptyPen',
  type: 'EmptyPen',
  empty() { return true; },
  _empty: true,
  _type : 'Empty',
  _width : new Magnitude(0,'mm'),

  getType() {
    return 'Empty' ;
  },

  get width() {
    return new Magnitude(0,'mm');
  },
  get color() {
    return Color.Transparent();
  },
  get name() {
    return null;
  },
  get tag() {
    return null;
  },
  get label() {
    return null;
  },

  buildXml() {

  },
  setupXml() {

  },


  isEqual( other ) {
    return other instanceof EmptyPen;
  },
  isNear( other/*,eps*/) {
    return other._empty;
  },

  getColors( colors = [] ) {
    return colors;
  },

  invalidate() {}
});
Xml.addToXml(EmptyPen);

Pen.empty = new EmptyPen();
Pen.empty._owner = {};

Pen.def = function(p,def) { return p._empty ? def : p ; };

Pen.arrayFromXml = function( node ) {
  const pcNode = Xml.child(node,'Pens');
  if( pcNode ) {
    node = pcNode;
  }
  return Xml.getObjectArray(node,Pen,'Pen'); // [TODO] Support CutPen
};

Pen.arrayBuildXml = function(node,v) {
  if( isEmpty(v) ) {
    return;
  }
  const pcNode = Xml.appendElement(node,'Pens');
  Xml.addObjectArray(pcNode,v);
};

// Pen.fromXml checks all types of pens
Pen._fromXml = function( node ) { return Xml.getObject(node,Pen,'Pen'); };
Pen.fromXml = function( node ) {
  return Pen._fromXml(node) ||
         CutPen.fromXml(node) ||
         DisplayPen.fromXml(node) ||
         MaskPen.fromXml(node) ||
         Pen.empty ;
};
Pen.fromXmlString = function(xmlString) {
  const domFigure = Xml.fromString(xmlString);
  return Pen.fromXml(domFigure.documentElement);
};

Xml.addFromBuild(Pen,'Pen');
Xml.addToXml(Pen);

Xml.Parser['Pen'  ] = Xml.makeObjectParser(Pen);
Xml.Parser['Pen[]'] = Xml.makeArrayObjectParser(Pen);



/** WEBPACK FOOTER **
 ** ./src/cdl/style/pen.js
 **/